import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Form, InputNumber, Select, Button, Space, Table, Tag, message, Popconfirm } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const BudgetPlanner = () => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const { token } = useAuth();

  const columns = [
    { title: 'Period', dataIndex: 'period', key: 'period' },
    { title: 'Channel', dataIndex: 'channel', key: 'channel' },
    { title: 'Budget (USD)', dataIndex: 'budget', key: 'budget', render: v => `$${Number(v)?.toLocaleString?.() || v}` },
    { title: 'Status', dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'planned' ? 'blue' : 'green'}>{s}</Tag>, width: 120 },
    {
      title: 'Actions', key: 'actions', width: 140, render: (_, record) => (
        <Space>
          <Popconfirm title="Delete this budget?" onConfirm={() => onDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        setLoading(true);
        const res = await fetch('/api/budgets', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load budgets');
        const data = await res.json();
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        message.error(e?.message || 'Failed to load budgets');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const onAdd = async () => {
    try {
      const values = await form.validateFields();
      if (!token) throw new Error('Not authenticated');
      setAdding(true);
      const res = await fetch('/api/budgets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error('Failed to add');
      const created = await res.json();
      setRows(prev => [created, ...prev]);
      form.resetFields();
      message.success('Budget added');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const onDelete = async (record) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/budgets/${record.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      setRows(prev => prev.filter(r => r.id !== record.id));
      message.success('Budget deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Budgets' }, { title: 'Planner' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Budget Planner</Title>
        <Space>
          <Button type="primary" onClick={onAdd} loading={adding}>Add</Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Plan monthly budgets per channel.</Paragraph>
        <Form form={form} layout="inline">
          <Form.Item label="Period" name="period" rules={[{ required: true }]}> 
            <Select placeholder="Select period" style={{ width: 180 }}>
              <Option value="August 2025">August 2025</Option>
              <Option value="September 2025">September 2025</Option>
              <Option value="October 2025">October 2025</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Channel" name="channel" rules={[{ required: true }]}> 
            <Select placeholder="Select channel" style={{ width: 180 }}>
              <Option value="Facebook">Facebook</Option>
              <Option value="Google Ads">Google Ads</Option>
              <Option value="LinkedIn">LinkedIn</Option>
              <Option value="TikTok">TikTok</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Budget" name="budget" rules={[{ required: true }]}> 
            <InputNumber min={0} prefix="$" style={{ width: 160 }} />
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>
    </div>
  );
};

export default BudgetPlanner;
